import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLANS } from "@/lib/constants";
import { CheckoutDisclosure } from "@/components/FundingDisclosure";
import { startCheckout, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const YEARLY = (p) => ({ ...p, key: p.key.replace("_monthly", "_yearly"), price: p.price * 10, period: "/yr", perMonth: Math.round((p.price * 10 / 12) * 100) / 100, save: p.price * 2 });

export function PricingCards() {
  const { user, hasPkg } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [yearly, setYearly] = useState(false);
  const choose = async (key) => {
    if (!user) return navigate("/login?mode=register");
    setBusy(key);
    try {
      await startCheckout(key);
    } catch (e) {
      toast.error(formatErr(e));
      setBusy(null);
    }
  };
  return (
    <div>
    <div className="flex justify-center mb-8">
      <div className="inline-flex items-center rounded-full border border-slate-300 bg-white p-1 text-sm" role="tablist" data-testid="billing-period-toggle">
        <button type="button" onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full transition-colors ${!yearly ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="billing-monthly">Monthly</button>
        <button type="button" onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full transition-colors ${yearly ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="billing-yearly">Yearly <span className={yearly ? "text-emerald-300" : "text-emerald-700"}>· 2 months free</span></button>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {PLANS.map((m) => {
        const p = yearly ? YEARLY(m) : m;
        const owned = hasPkg(p.pkg);
        return (
          <div key={p.key} data-testid={`plan-card-${p.key}`}
            className={`card-lift rounded-lg border p-8 flex flex-col ${p.popular ? "bg-slate-900 text-white border-slate-900 ring-4 ring-emerald-400/60 lg:-translate-y-2" : "bg-white border-slate-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className={`font-mono text-[11px] uppercase tracking-[0.18em] ${p.popular ? "text-emerald-400" : "text-emerald-600"}`}>{p.area}</span>
              {p.popular && <span className="flex gap-1.5"><span className="text-[11px] font-semibold bg-emerald-500 text-slate-950 px-2 py-0.5 rounded" data-testid="plan-recommended-badge">Recommended</span><span className="text-[11px] font-semibold bg-amber-500 text-white px-2 py-0.5 rounded">Best value</span></span>}
            </div>
            <h3 className="font-display text-2xl font-extrabold mt-4">{p.name}</h3>
            <p className={`text-sm mt-2 ${p.popular ? "text-slate-300" : "text-slate-600"}`}>{p.blurb}</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display text-5xl font-extrabold">${p.price}</span>
              <span className={p.popular ? "text-slate-400" : "text-slate-500"}>{p.period}</span>
            </div>
            {p.perMonth && <p className={`text-sm mt-1 ${p.popular ? "text-slate-300" : "text-slate-600"}`} data-testid={`plan-yearly-note-${m.key}`}>${p.perMonth.toFixed(2)}/mo billed yearly · you save ${p.save}</p>}
            {p.anchor && !yearly && <p className={`text-sm mt-2 font-medium ${p.popular ? "text-emerald-300" : "text-emerald-700"}`} data-testid={`plan-anchor-${p.key}`}>{p.anchor}</p>}
            <ul className="mt-6 space-y-3 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex gap-2 text-sm"><Check className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />{f}</li>
              ))}
            </ul>
            <Button onClick={() => choose(p.key)} disabled={owned || !!busy} data-testid={`plan-buy-${p.key}`}
              className={`mt-8 h-11 ${p.popular ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-slate-900 hover:bg-slate-800"}`}>
              {busy === p.key && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {owned ? "Active" : user ? "Subscribe" : "Get started"}
            </Button>
            <p className={`text-xs mt-3 text-center ${p.popular ? "text-slate-300" : "text-slate-600"}`} data-testid={`plan-free-line-${m.key}`}>Grant search is free, no credit card required. 14-day money-back guarantee on your first plan; after 14 days all sales are final.</p>
            <CheckoutDisclosure dark={p.popular} className="mt-3" id={`plan-${m.key}`} />
          </div>
        );
      })}
    </div>
    </div>
  );
}
